
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { AlertTriangle, HardDrive, Zap } from "lucide-react";
import { useProfileStorage } from "@/hooks/useProfileStorage";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";

interface StorageWidgetProps {
  showUpgradeButton?: boolean;
  compact?: boolean;
}

const StorageWidget = ({ showUpgradeButton = true, compact = false }: StorageWidgetProps) => {
  const { 
    storageUsage, 
    getStoragePercentage, 
    isStorageWarning, 
    isStorageCritical,
    formatStorageSize,
    isLoading 
  } = useProfileStorage();
  const { t, i18n } = useTranslation('profile');

  if (isLoading || !storageUsage) {
    return (
      <Card className="w-full uplivion-card">
        <CardContent className="p-4">
          <div className="animate-pulse space-y-2">
            <div className="h-4 bg-muted rounded w-3/4"></div>
            <div className="h-2 bg-muted rounded w-full"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const plan = storageUsage.subscription_plan;
  const usedMB = storageUsage.storage_used_mb;
  const limitMB = plan?.storage_limit_mb || 100;
  const percentage = getStoragePercentage();
  const isEnglish = i18n.language === 'en';

  const planName = isEnglish ? (plan?.name_en || plan?.name) : plan?.name;

  if (compact) {
    return (
      <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg">
        <HardDrive className="w-4 h-4 text-muted-foreground" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">
              {formatStorageSize(usedMB)} / {formatStorageSize(limitMB)}
            </span>
            {isStorageCritical && <AlertTriangle className="w-4 h-4 text-destructive" />}
          </div>
          <Progress 
            value={percentage} 
            className="h-1 mt-1"
            indicatorClassName={
              isStorageCritical ? "bg-destructive" : 
              isStorageWarning ? "bg-orange-500" : "bg-primary"
            }
          />
        </div>
      </div>
    );
  }

  return (
    <Card className="w-full uplivion-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <HardDrive className="w-5 h-5" />
            {t('storage.title', 'Speicher')}
          </CardTitle>
          <Badge variant="outline">{planName}</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span>{t('storage.used', 'Verwendet')}</span>
            <span className="font-medium">
              {formatStorageSize(usedMB)} / {formatStorageSize(limitMB)}
            </span>
          </div>
          <Progress 
            value={percentage}
            className="h-2"
            indicatorClassName={
              isStorageCritical ? "bg-destructive" : 
              isStorageWarning ? "bg-orange-500" : "bg-primary"
            }
          />
          <div className="text-xs text-muted-foreground">
            {percentage.toFixed(1)}% {t('storage.ofLimit', 'des Limits verwendet')}
          </div>
        </div>

        {(isStorageWarning || isStorageCritical) && (
          <div className={`flex items-start gap-2 p-3 rounded-lg ${
            isStorageCritical ? 'bg-destructive/10 border border-destructive/20' : 'bg-orange-50 border border-orange-200'
          }`}>
            <AlertTriangle className={`w-4 h-4 mt-0.5 ${
              isStorageCritical ? 'text-destructive' : 'text-orange-500'
            }`} />
            <div className="text-sm">
              <p className="font-medium">
                {isStorageCritical 
                  ? t('storage.criticalWarning', 'Speicher fast voll!') 
                  : t('storage.warning', 'Speicher wird knapp')
                }
              </p>
              <p className="text-muted-foreground">
                {isStorageCritical
                  ? t('storage.criticalMessage', 'Sie können keine neuen Inhalte hinzufügen.')
                  : t('storage.warningMessage', 'Erwägen Sie ein Upgrade für mehr Speicher.')
                }
              </p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-muted-foreground">{t('storage.entries', 'Einträge')}</p>
            <p className="font-medium">
              {storageUsage.entries_count}
              {plan?.max_entries && ` / ${plan.max_entries}`}
            </p>
          </div>
          <div>
            <p className="text-muted-foreground">{t('storage.images', 'Bilder')}</p>
            <p className="font-medium">
              {storageUsage.images_count}
              {plan?.max_images && ` / ${plan.max_images}`}
            </p>
          </div>
        </div>

        {showUpgradeButton && plan?.is_default && (
          <Button className="w-full" variant="outline">
            <Zap className="w-4 h-4 mr-2" />
            {t('storage.upgrade', 'Plan upgraden')}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default StorageWidget;
